/**
 * Pure progress model for the athlete Programs view (plan 3g).
 *
 * Mirrors PeakElite's `programSessionLogged.ts`: a program session counts as
 * completed when `status === "complete"`; total is the number of sessions
 * linked to the program. The week/day grid is derived from `plannedDate`
 * relative to the program's start, never from `programLinkage.weekNumber /
 * dayNumber`, which drift once the athlete reschedules (decision 3).
 *
 * Free of React and Firestore so it is unit testable.
 */
import { Session } from "../types/post";

export type ProgramProgressSession = Pick<Session, "plannedDate" | "status"> &
  Partial<Pick<Session, "programLinkage">> & { id: string };

export type ProgramProgressState = "notStarted" | "inProgress" | "finished";

export interface ProgramProgress {
  total: number;
  completed: number;
  /** 0–100, rounded; 0 when total is 0. */
  percent: number;
  /** 1-based week containing today, clamped to the program; null before start. */
  currentWeek: number | null;
  state: ProgramProgressState;
  /** YYYY-MM-DD, Monday of week 1. */
  startDate: string;
  /** YYYY-MM-DD, last day of the final week (inclusive). */
  endDate: string;
}

export interface ProgramAssignmentLike {
  type?: string;
  targetId?: string;
  startDate?: string;
  assignedBy?: string;
}

export interface ProgramGridDay<T extends ProgramProgressSession> {
  /** 1 = Monday … 7 = Sunday. */
  dayNumber: number;
  /** YYYY-MM-DD */
  date: string;
  sessions: T[];
}

export interface ProgramGridWeek<T extends ProgramProgressSession> {
  weekNumber: number;
  startDate: string;
  endDate: string;
  days: ProgramGridDay<T>[];
}

export interface ProgramGrid<T extends ProgramProgressSession> {
  weeks: ProgramGridWeek<T>[];
  /** Scheduled outside `[startDate, startDate + durationWeeks*7)` (moved). */
  outside: T[];
  /** No usable `plannedDate` (e.g. proposed / unscheduled coach sessions). */
  unscheduled: T[];
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const toLocalDateString = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const parseLocalDate = (value?: string | null): Date | null => {
  if (!value || !DATE_RE.test(value)) return null;
  const [y, m, d] = value.split("-").map(Number);
  const parsed = new Date(y, m - 1, d);
  if (Number.isNaN(parsed.getTime())) return null;
  // Reject overflowed dates such as 2026-02-31.
  if (toLocalDateString(parsed) !== value) return null;
  return parsed;
};

export const addDaysToDateString = (value: string, days: number): string => {
  const date = parseLocalDate(value) ?? new Date();
  date.setDate(date.getDate() + days);
  return toLocalDateString(date);
};

/** Monday on or before `value`. */
export const mondayOf = (value: string): string => {
  const date = parseLocalDate(value);
  if (!date) return value;
  const day = date.getDay();
  date.setDate(date.getDate() + (day === 0 ? -6 : 1 - day));
  return toLocalDateString(date);
};

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export const dayOffset = (from: string, to: string): number | null => {
  const a = parseLocalDate(from);
  const b = parseLocalDate(to);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
};

export const clampDurationWeeks = (value: unknown): number => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.min(12, Math.max(1, Math.trunc(n)));
};

const isRejected = (s: Pick<Session, "status">) => s.status === "rejected";

/**
 * Where week 1 starts. Order (audit §3 step 3): the athlete's own individual
 * assignment → the first assignment → the program's `startDate` → the Monday
 * on or before the earliest scheduled session → the Monday of today.
 * Always normalised to a Monday.
 */
export const resolveProgramStartDate = (
  program: { startDate?: string; assignments?: ProgramAssignmentLike[] },
  sessions: ProgramProgressSession[],
  athleteId?: string,
  today: Date = new Date(),
): string => {
  const assignments = program.assignments ?? [];
  const own = athleteId
    ? assignments.find(
        (a) => a.type === "individual" && a.targetId === athleteId,
      )
    : undefined;
  const candidates = [
    own?.startDate,
    assignments.find((a) => parseLocalDate(a.startDate))?.startDate,
    program.startDate,
  ];
  for (const candidate of candidates) {
    if (parseLocalDate(candidate)) return mondayOf(candidate as string);
  }
  const earliest = sessions
    .filter((s) => !isRejected(s))
    .map((s) => s.plannedDate)
    .filter((d) => parseLocalDate(d))
    .sort()[0];
  if (earliest) return mondayOf(earliest);
  return mondayOf(toLocalDateString(today));
};

export interface ProgramProgressInput {
  startDate: string;
  durationWeeks: number;
  /**
   * `program.linkedSessions.length`. Used as the total only when no athlete
   * sessions were found (e.g. a copy whose sessions have not synced yet).
   */
  linkedSessionCount?: number;
}

export const computeProgramProgress = (
  program: ProgramProgressInput,
  sessions: ProgramProgressSession[],
  today: Date = new Date(),
): ProgramProgress => {
  const durationWeeks = clampDurationWeeks(program.durationWeeks);
  const startDate = mondayOf(program.startDate);
  const endDate = addDaysToDateString(startDate, durationWeeks * 7 - 1);
  const live = sessions.filter((s) => !isRejected(s));
  const total = live.length > 0 ? live.length : program.linkedSessionCount ?? 0;
  const completed = Math.min(
    live.filter((s) => s.status === "complete").length,
    total,
  );
  const percent = total === 0 ? 0 : Math.round((completed / total) * 100);

  const todayStr = toLocalDateString(today);
  const offset = dayOffset(startDate, todayStr) ?? 0;
  let state: ProgramProgressState;
  let currentWeek: number | null;
  if (offset < 0) {
    state = "notStarted";
    currentWeek = null;
  } else if (offset >= durationWeeks * 7 || (total > 0 && completed === total)) {
    state = "finished";
    currentWeek = Math.min(durationWeeks, Math.floor(offset / 7) + 1);
  } else {
    state = "inProgress";
    currentWeek = Math.floor(offset / 7) + 1;
  }

  return { total, completed, percent, currentWeek, state, startDate, endDate };
};

/**
 * Week/day grid keyed by `plannedDate`. Every week has all seven days so the
 * detail view can show rest days; sessions outside the window land in
 * `outside`, sessions without a date in `unscheduled`.
 */
export const buildProgramGrid = <T extends ProgramProgressSession>(
  program: { startDate: string; durationWeeks: number },
  sessions: T[],
): ProgramGrid<T> => {
  const durationWeeks = clampDurationWeeks(program.durationWeeks);
  const startDate = mondayOf(program.startDate);
  const weeks: ProgramGridWeek<T>[] = Array.from(
    { length: durationWeeks },
    (_, w) => {
      const weekStart = addDaysToDateString(startDate, w * 7);
      return {
        weekNumber: w + 1,
        startDate: weekStart,
        endDate: addDaysToDateString(weekStart, 6),
        days: Array.from({ length: 7 }, (_, d) => ({
          dayNumber: d + 1,
          date: addDaysToDateString(weekStart, d),
          sessions: [] as T[],
        })),
      };
    },
  );
  const outside: T[] = [];
  const unscheduled: T[] = [];

  const ordered = [...sessions]
    .filter((s) => !isRejected(s))
    .sort((a, b) => (a.plannedDate || "").localeCompare(b.plannedDate || ""));

  for (const session of ordered) {
    const offset = dayOffset(startDate, session.plannedDate);
    if (offset === null) {
      unscheduled.push(session);
      continue;
    }
    if (offset < 0 || offset >= durationWeeks * 7) {
      outside.push(session);
      continue;
    }
    weeks[Math.floor(offset / 7)].days[offset % 7].sessions.push(session);
  }

  return { weeks, outside, unscheduled };
};

/**
 * True when the session's current date disagrees with the week/day the coach
 * stamped on it, i.e. it has been moved since assignment.
 */
export const isSessionMoved = (
  startDate: string,
  session: ProgramProgressSession,
): boolean => {
  const linkage = session.programLinkage;
  if (!linkage?.weekNumber || !linkage?.dayNumber) return false;
  const offset = dayOffset(mondayOf(startDate), session.plannedDate);
  if (offset === null) return false;
  const expected = (linkage.weekNumber - 1) * 7 + (linkage.dayNumber - 1);
  return offset !== expected;
};

/** Short human label for the progress state / current week. */
export const describeProgramProgress = (progress: ProgramProgress): string => {
  if (progress.state === "notStarted") return "Not started";
  if (progress.state === "finished") return "Finished";
  return `Week ${progress.currentWeek}`;
};
